import React, { useState } from "react";
import Icons from "../../icons";

/* For you › Test Runs › review: the result of an assigned run, and what has
   to be said before it goes to the run in Jira.
     PASSED            → nothing more (a comment is optional)
     FAILED / BLOCKED  → the Failure Cause and a comment saying what was seen
     Product defect    → a Bug: raise one above ("Raise a defect") or give its key
   Submit writes to the existing run only. */

export const FAILURE_CAUSES = ["Product defect", "Test defect", "Requirement defect", "Environment or data"];

const RESULT_TONE = {
  PASSED: "bg-emerald-50 text-emerald-700 ring-emerald-600/20",
  FAILED: "bg-rose-50 text-rose-700 ring-rose-600/20",
  BLOCKED: "bg-amber-50 text-amber-800 ring-amber-600/30",
};

/* What still stops Submit, or "" when it can go. */
export function submitProblem({ result, comment, failureCause, bugKey }) {
  if (result === "PASSED") return "";
  if (!failureCause) return "Choose the Failure Cause.";
  if (!comment.trim()) return "Say what was seen, in the comment.";
  if (failureCause === "Product defect" && !bugKey.trim()) return "A product defect needs a Bug: raise one or give its key.";
  if (bugKey.trim() && !/^[A-Za-z][A-Za-z0-9_]*-\d+$/.test(bugKey.trim())) return "The Bug key looks wrong (e.g. PSR-3001).";
  return "";
}

export default function TestRunSubmitPanel({ row, bugFromRun = "", busy = false, onSubmit }) {
  const result = row?.local?.run_result || "";
  const te = row?.execution || {};
  const [comment, setComment] = useState("");
  const [failureCause, setFailureCause] = useState(row?.local?.failure_cause || "");
  const [bugTyped, setBugTyped] = useState("");
  const bugKey = bugTyped || bugFromRun || "";
  const problem = submitProblem({ result, comment, failureCause, bugKey });
  const notPassed = result !== "PASSED";
  const field = "w-full rounded-lg border border-slate-300 bg-card px-3 py-2 text-sm text-slate-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/25";

  return (
    <section aria-label="Submit to Jira" className="space-y-4 rounded-2xl border border-brand-200 bg-card p-5 shadow-sm">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-sm font-semibold text-slate-900">Submit to the test run in Jira</h2>
        <span className={`rounded-full px-2.5 py-0.5 text-[12px] font-semibold ring-1 ring-inset ${RESULT_TONE[result] || ""}`}>
          {result || "No result"}
        </span>
      </div>

      <dl className="grid gap-x-6 gap-y-1 text-[13px] text-slate-600 sm:grid-cols-2">
        <div><dt className="inline font-semibold text-slate-800">Test: </dt><dd className="inline">{row?.test?.key} · {row?.test?.summary}</dd></div>
        <div><dt className="inline font-semibold text-slate-800">Execution: </dt><dd className="inline">{te.key}</dd></div>
        <div><dt className="inline font-semibold text-slate-800">Planned version: </dt><dd className="inline">{(te.planned_versions || []).join(", ") || "—"}</dd></div>
        <div><dt className="inline font-semibold text-slate-800">Affects version: </dt><dd className="inline">{(te.affects_versions || []).join(", ") || "—"}</dd></div>
        <div><dt className="inline font-semibold text-slate-800">Fix version: </dt><dd className="inline">{(te.fix_versions || []).join(", ") || "—"}</dd></div>
        <div><dt className="inline font-semibold text-slate-800">Build Number: </dt><dd className="inline">{te.build_number || "—"}</dd></div>
        <div><dt className="inline font-semibold text-slate-800">Test Environment: </dt><dd className="inline">{(te.test_environments || []).join(", ") || "—"}</dd></div>
      </dl>

      {notPassed && (
        <div className="space-y-1.5">
          <label htmlFor="tr-cause" className="text-sm font-semibold text-slate-800">
            Failure Cause <span className="text-rose-500">*</span>
          </label>
          <select id="tr-cause" value={failureCause} onChange={(e) => setFailureCause(e.target.value)} className={field}>
            <option value="">Choose…</option>
            {FAILURE_CAUSES.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>
      )}

      {notPassed && failureCause === "Product defect" && (
        <div className="space-y-1.5">
          <label htmlFor="tr-bug" className="text-sm font-semibold text-slate-800">
            Bug <span className="text-rose-500">*</span>
          </label>
          {bugFromRun ? (
            <p className="text-sm text-slate-700">{bugFromRun} — raised from this run.</p>
          ) : (
            <>
              <input
                id="tr-bug"
                value={bugTyped}
                onChange={(e) => setBugTyped(e.target.value.toUpperCase())}
                placeholder="PSR-3001"
                autoComplete="off"
                className={field}
              />
              <p className="text-xs text-slate-500">Raise one with “Raise a defect” above, or give an existing Bug's key.</p>
            </>
          )}
        </div>
      )}

      <div className="space-y-1.5">
        <label htmlFor="tr-comment" className="text-sm font-semibold text-slate-800">
          Comment {notPassed ? <span className="text-rose-500">*</span> : <span className="font-normal text-slate-400">(optional)</span>}
        </label>
        <textarea
          id="tr-comment"
          rows={3}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder={notPassed ? "What was seen: the step, what was expected, what happened." : ""}
          className={field}
        />
      </div>

      {row?.local?.submit_error && (
        <p role="alert" className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">
          The last submit stopped: {row.local.submit_error}
        </p>
      )}

      <div className="flex flex-wrap items-center justify-end gap-3">
        {problem && <span className="text-xs text-slate-500">{problem}</span>}
        <button
          type="button"
          disabled={!!problem || busy}
          onClick={() => onSubmit({ comment: comment.trim(), failure_cause: notPassed ? failureCause : "", bug_key: bugKey.trim() })}
          className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400"
        >
          <Icons.Check className="h-4 w-4" />
          {row?.local?.submit_state === "error" ? "Retry submit" : "Submit"}
        </button>
      </div>
    </section>
  );
}
